import React from 'react';
import { MapPin, Loader2, AlertCircle } from 'lucide-react';
import Button from '../Ui/Button';
import Badge from '../Ui/Badge';
import { useNavigate } from 'react-router-dom';
import { Seeker } from '../types'; // Import Seeker type

interface SeekerStatusProps {
    seekers: Seeker[]; // Use the existing Seeker type
    theme: 'light' | 'dark';
    loading?: boolean;
    error?: string;
}

const SeekerStatus = ({ seekers, theme, loading, error }: SeekerStatusProps) => {
    const navigate = useNavigate();

    if (loading) {
        return (
            <div className="max-w-3xl mx-auto py-12 px-6">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className={`text-3xl font-black ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>Dashboard</h2>
                        <p className={theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}>Track your applications in real-time</p>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/')} theme={theme}>Back</Button>
                </div>
                <div className="flex flex-col items-center justify-center py-20">
                    <Loader2 className="w-12 h-12 animate-spin text-violet-500 mb-4" />
                    <p className={theme === 'dark' ? 'text-zinc-300' : 'text-zinc-600'}>Loading applications...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-3xl mx-auto py-12 px-6">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className={`text-3xl font-black ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>Dashboard</h2>
                        <p className={theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}>Track your applications in real-time</p>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/')} theme={theme}>Back</Button>
                </div>
                <div className="flex flex-col items-center justify-center py-20">
                    <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
                    <p className={theme === 'dark' ? 'text-zinc-300 mb-2' : 'text-zinc-600 mb-2'}>{error}</p>
                    <Button variant="primary" onClick={() => window.location.reload()} theme={theme}>
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    if (seekers.length === 0) {
        return (
            <div className="max-w-3xl mx-auto py-12 px-6">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className={`text-3xl font-black ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>Dashboard</h2>
                        <p className={theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}>Track your applications in real-time</p>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/')} theme={theme}>Back</Button>
                </div>
                <div className="text-center py-20">
                    <p className={theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}>No applications found. Apply for a job to see your status here.</p>
                    <Button variant="primary" onClick={() => navigate('/apply')} className="mt-4">
                        Apply Now
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto py-12 px-6">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className={`text-3xl font-black ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>Dashboard</h2>
                    <p className={theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}>Track your applications in real-time</p>
                </div>
                <Button variant="outline" onClick={() => navigate('/')} theme={theme}>Back</Button>
            </div>

            <div className="grid gap-4">
                {seekers.map((app) => (
                    <div key={app.id} className={`group ${theme === 'dark' ? 'bg-zinc-900 border-zinc-800 hover:border-violet-900' : 'bg-white border-zinc-100 hover:border-violet-200'} p-6 rounded-2xl border shadow-sm hover:shadow-lg transition-all duration-300 relative overflow-hidden`}>
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-violet-500 to-fuchsia-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div className="flex items-start gap-4">
                                <div className={`w-12 h-12 rounded-xl ${theme === 'dark' ? 'bg-zinc-800' : 'bg-zinc-100'} flex items-center justify-center ${theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'} font-bold`}>
                                    {app.role.charAt(0)}
                                </div>
                                <div>
                                    <h3 className={`font-bold text-lg ${theme === 'dark' ? 'text-white group-hover:text-violet-400' : 'text-zinc-900 group-hover:text-violet-600'} transition-colors`}>{app.role}</h3>
                                    <p className={`text-sm flex items-center gap-2 mt-1 ${theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                        <MapPin className="w-3 h-3" /> {app.location}
                                        <span className={`w-1 h-1 rounded-full ${theme === 'dark' ? 'bg-zinc-600' : 'bg-zinc-300'}`}></span>
                                        {app.exp} Exp
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 pl-16 sm:pl-0">
                                <Badge status={app.status} />
                                <span className={`text-xs ${theme === 'dark' ? 'text-zinc-400' : 'text-zinc-400'}`}>Applied: {new Date().toLocaleDateString()}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SeekerStatus;